import React, { useEffect, useRef, useState } from 'react';

/**
 * Anchored popover menu used to keep secondary toolbar actions off the main row.
 *
 * `trigger` is a render prop so callers control the button's look:
 *   trigger={({ open, toggle }) => <button onClick={toggle} />}
 *
 * `items` entries are either `{ separator: true }` or:
 *   { id, label, icon, onClick, hint, danger, hidden }
 */
const DropdownMenu = ({ trigger, items = [], align = 'right', className = '' }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const visible = items.filter(item => !item.hidden);

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {trigger({ open, toggle: () => setOpen(prev => !prev) })}

            {open && (
                <div
                    role="menu"
                    className={`absolute top-full mt-2 z-50 min-w-[14rem] rounded-xl border border-slate-200 bg-white py-1 shadow-xl ${align === 'right' ? 'right-0' : 'left-0'}`}
                >
                    {visible.map((item, index) => (
                        item.separator ? (
                            <div key={`sep-${index}`} className="my-1 h-px bg-slate-100" />
                        ) : (
                            <button
                                key={item.id || item.label}
                                id={item.id}
                                role="menuitem"
                                onClick={() => { setOpen(false); item.onClick?.(); }}
                                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm font-medium transition-colors ${item.danger
                                    ? 'text-red-600 hover:bg-red-50'
                                    : 'text-slate-700 hover:bg-slate-100'
                                    }`}
                            >
                                {item.icon}
                                <span className="flex-1 truncate">{item.label}</span>
                                {item.hint && <span className="text-xs text-slate-400">{item.hint}</span>}
                            </button>
                        )
                    ))}
                </div>
            )}
        </div>
    );
};

export default DropdownMenu;
